import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge, SectionTitle, Field } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";
import { MapPin, Cloud, Loader2, ArrowRight, Search, Users, Plane, Wrench, Building2, Briefcase } from "lucide-react";
import Globe from "react-globe.gl";
import BackToCockpit from "@/components/BackToCockpit";

const ROLES = [
  { key: "all",              label: "All Members",     color: "#4B9CFF", icon: Users },
  { key: "pilot",            label: "Pilots",          color: "#2ED573", icon: Plane },
  { key: "flight_attendant", label: "Flight Attendants", color: "#B47CFF", icon: Users },
  { key: "amt",              label: "Mechanics",       color: "#F4C443", icon: Wrench },
  { key: "fbo",              label: "FBOs",            color: "#E8A257", icon: Building2 },
  { key: "charter_operator", label: "Operators",       color: "#EB5757", icon: Plane },
  { key: "aircraft_owner",   label: "Owners",          color: "#6DBE8F", icon: Plane },
  { key: "aircraft_sales",   label: "AV/EXEC",         color: "#4B9CFF", icon: Briefcase },
];

const TONE_HEX = { green:"#2ED573", yellow:"#F4C443", red:"#EB5757", purple:"#B47CFF", gray:"#4B9CFF" };

export default function AirportDetail() {
  const { icao } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const isWelcome = sp.get("welcome") === "1";
  const globeRef = useRef();

  const [airport, setAirport] = useState(null);
  const [wx, setWx] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [points, setPoints] = useState([]);
  const [tracked, setTracked] = useState(null);

  const [gwidth, setGwidth] = useState(560);
  const containerRef = useRef();
  useEffect(() => {
    const upd = () => setGwidth(containerRef.current?.offsetWidth || 560);
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  useEffect(() => {
    setAirport(null); setWx(null);
    api.get(`/airports/${icao}`).then((r) => setAirport(r.data)).catch(() => setAirport({ error: true }));
    api.get(`/weather/${icao}`).then((r) => setWx(r.data)).catch(() => setWx(null));
  }, [icao]);

  useEffect(() => {
    api.get(`/globe/points${roleFilter && roleFilter !== "all" ? `?role=${roleFilter}` : ""}`)
      .then((r) => setPoints(r.data.points || []))
      .catch(() => setPoints([]));
  }, [roleFilter]);

  // Center the globe on the airport itself as soon as we have coordinates.
  useEffect(() => {
    if (globeRef.current && airport && !airport.error && airport.lat != null && airport.lng != null) {
      globeRef.current.pointOfView({ lat: airport.lat, lng: airport.lng, altitude: 1.9 }, 1200);
    }
  }, [airport?.icao, airport?.lat, airport?.lng]);

  const tone = wx?.tone || "gray";
  const toneHex = TONE_HEX[tone] || TONE_HEX.gray;

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-4">
        <BackToCockpit />
        {isWelcome && (
          <div className="mb-5 rounded-lg border-2 border-emerald-400 bg-emerald-400/[0.08] p-4 flex items-center gap-3" data-testid="welcome-banner">
            <div className="h-10 w-10 rounded-full bg-emerald-400 flex items-center justify-center animate-pulse">
              <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5"><path d="M20 6L9 17l-5-5" stroke="#0A1F14" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="flex-1">
              <div className="mono text-sm font-bold text-emerald-400 tracking-wider">WELCOME TO PJP · YOUR PIN JUST LANDED</div>
              <div className="text-xs text-white/70 mt-0.5">Scroll down — you're one of the pins on the interactive globe below. Explore other members, check the weather, drop notes on your favorite FBOs.</div>
            </div>
            <button onClick={() => nav("/dashboard")} className="btn">Go to cockpit →</button>
          </div>
        )}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-cyan mb-1">// AIRPORT LOOKUP</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-none flex items-baseline gap-3">
              <span className="mono">{icao?.toUpperCase()}</span>
              {airport && !airport.error && airport.iata && <span className="text-sm mono text-white/40">IATA {airport.iata}</span>}
            </h1>
            {airport && !airport.error && (
              <div className="mt-2 text-white/70 flex items-center gap-2 flex-wrap">
                <MapPin className="h-3.5 w-3.5" />
                <span>{airport.name}</span>
                <span className="text-white/40">·</span>
                <span>{airport.city}, {airport.country}</span>
              </div>
            )}
          </div>
          <div className="w-full md:w-72">
            <Field label="Jump to another airport">
              <AirportInput testId="ap-jump" value="" onChange={(v) => v && nav(`/airports/${v}`)} placeholder="Type city or ICAO…" />
            </Field>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-10 grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Globe */}
        <div ref={containerRef} data-testid="globe-wrap">
          <Card className="!p-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="overline text-cyan">// PJP NETWORK · {points.length} PINS</div>
              <div className="flex flex-wrap gap-1.5">
                {ROLES.map((r) => (
                  <button
                    key={r.key}
                    data-testid={`globe-filter-${r.key}`}
                    onClick={() => setRoleFilter(r.key)}
                    className={`px-2.5 py-1 rounded-full mono text-[10px] border transition-all ${roleFilter === r.key ? "text-white" : "text-white/50 hover:text-white/80 border-white/10"}`}
                    style={roleFilter === r.key ? { borderColor: r.color, background: `${r.color}20`, color: r.color } : {}}
                  >
                    {r.label.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ height: 520, background: "#050810" }}>
              <Globe
                ref={globeRef}
                width={gwidth}
                height={520}
                backgroundColor="rgba(0,0,0,0)"
                globeImageUrl="https://unpkg.com/three-globe/example/img/earth-night.jpg"
                bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
                atmosphereColor="#4B9CFF"
                atmosphereAltitude={0.14}
                pointsData={points}
                pointLat="lat"
                pointLng="lng"
                pointColor={(d) => ROLES.find((r) => r.key === d.role)?.color || "#4B9CFF"}
                pointRadius={0.35}
                pointAltitude={0.02}
                pointLabel={(d) => `<div style="background:rgba(0,0,0,0.85);padding:8px 10px;border:1px solid #4B9CFF;border-radius:6px;color:white;font-family:monospace;font-size:11px"><b>${d.name}</b><br/>${d.role.toUpperCase()}<br/>${d.icao} · ${d.city || ""}</div>`}
                onPointClick={(d) => nav(`/airports/${d.icao}`)}
                ringsData={airport && !airport.error && airport.lat != null ? [{ lat: airport.lat, lng: airport.lng, color: TONE_HEX[tone] || "#4B9CFF" }] : []}
                ringColor={(d) => d.color}
                ringMaxRadius={4}
                ringPropagationSpeed={2}
                ringRepeatPeriod={1500}
              />
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {/* Weather */}
          <Card testId="wx-card" style={{ borderColor: `${toneHex}55` }}>
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="h-4 w-4" style={{ color: toneHex }} />
              <div className="overline" style={{ color: toneHex }}>// CURRENT METAR</div>
            </div>
            {!wx && <div className="text-white/50"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>}
            {wx && (
              <>
                <div className="mono text-3xl font-bold" style={{ color: toneHex }}>{wx.category || "—"}</div>
                <div className="mono text-[11px] text-white/60 mt-1">
                  {wx.visibility_sm !== null && wx.visibility_sm !== undefined ? `${wx.visibility_sm} SM` : "—"}
                  {" · "}
                  {wx.ceiling_ft ? `${wx.ceiling_ft} ft ceiling` : "no ceiling"}
                  {wx.temp_c !== null && wx.temp_c !== undefined ? ` · ${wx.temp_c}°C` : ""}
                  {wx.wind_kt ? ` · ${wx.wind_kt}kt wind` : ""}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {wx.has_snow && <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background:"rgba(180,124,255,0.15)", color:"#B47CFF", border:"1px solid rgba(180,124,255,0.4)"}}>SNOW</span>}
                  {wx.has_rain && <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background:"rgba(75,156,255,0.15)", color:"#4B9CFF", border:"1px solid rgba(75,156,255,0.4)"}}>RAIN</span>}
                  {!wx.has_snow && !wx.has_rain && wx.category === "VFR" && <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background:"rgba(46,213,115,0.15)", color:"#2ED573", border:"1px solid rgba(46,213,115,0.4)"}}>CLEAR</span>}
                </div>
                {wx.raw && (
                  <div className="mt-3 pt-3 border-t border-white/[0.06] text-[10.5px] mono text-white/60 whitespace-pre-wrap">{wx.raw}</div>
                )}
              </>
            )}
          </Card>

          {/* Pilot notes — placeholder for future */}
          <Card>
            <div className="overline text-cyan mb-3">// PILOT NOTES · FOOD · HOTELS</div>
            <AirportNotes icao={icao?.toUpperCase()} />
          </Card>

          <Card>
            <div className="overline text-cyan mb-2">// PJP MEMBERS BASED HERE</div>
            {points.filter((p) => p.icao === icao?.toUpperCase()).length === 0 ? (
              <div className="text-sm text-white/50 mono">No members based here yet.</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {points.filter((p) => p.icao === icao?.toUpperCase()).slice(0, 8).map((p) => (
                  <li key={p.user_id} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: ROLES.find(r=>r.key===p.role)?.color || "#4B9CFF"}} />
                    <span>{p.name}</span>
                    <span className="mono text-[10px] text-white/40 uppercase">{p.role}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>
      </section>
      <AirportNotesHelper />
    </div>
  );
}

// Notes component defined below — imported inline
import { useAuth as _useAuth } from "@/context/AuthContext";
import { toast as _toast } from "sonner";
import { ThumbsUp as _ThumbsUp, Plus as _Plus } from "lucide-react";

function AirportNotesHelper() { return null; } // placeholder; real UI is <AirportNotes /> above

const NOTE_CATEGORIES = ["FBO", "Food", "Hotel", "Customs", "Handling", "Curfew", "Ground", "Other"];
const CAT_COLOR = { FBO:"#4B9CFF", Food:"#E8A257", Hotel:"#B47CFF", Customs:"#EB5757", Handling:"#F4C443", Curfew:"#F4C443", Ground:"#6DBE8F", Other:"#4B9CFF" };

function AirportNotes({ icao }) {
  const { user } = _useAuth();
  const [notes, setNotes] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ category: "FBO", title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/airports/${icao}/notes`).then((r) => setNotes(r.data.notes || [])).catch(() => setNotes([]));
  useEffect(() => { if (icao) load(); }, [icao]);

  const submit = async () => {
    if (!form.title || !form.body) return _toast.error("Title + body required");
    setBusy(true);
    try {
      await api.post(`/airports/${icao}/notes`, { icao, ...form });
      _toast.success("Note posted");
      setForm({ category: "FBO", title: "", body: "" });
      setAdding(false);
      await load();
    } catch (e) { _toast.error(e?.response?.data?.detail || "Post failed"); } finally { setBusy(false); }
  };

  const upvote = async (n) => {
    try { await api.post(`/airports/${icao}/notes/${n.id}/upvote`); await load(); }
    catch (e) { _toast.error(e?.response?.data?.detail || "Upvote failed"); }
  };

  return (
    <div>
      {user ? (
        !adding ? (
          <button data-testid="btn-add-airport-note" onClick={() => setAdding(true)} className="btn btn-primary w-full justify-center mb-3">
            <_Plus className="h-3.5 w-3.5" /> Add a note
          </button>
        ) : (
          <div className="mb-3 space-y-2 p-3 rounded-md border border-cyan/25 bg-cyan/[0.04]">
            <select data-testid="note-category" className="input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
              {NOTE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="note-title" className="input" placeholder="Title (e.g. Signature is quickest)" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
            <textarea data-testid="note-body" className="input" rows={3} placeholder="Details — pilots want the truth. Prices, wait times, catering tips…" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAdding(false)} className="btn">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="btn-submit-note" className="btn btn-primary">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Post"}</button>
            </div>
          </div>
        )
      ) : (
        <div className="text-[11px] text-white/50 mono mb-3">Sign in to add a note.</div>
      )}
      {notes === null && <Loader2 className="h-4 w-4 animate-spin text-cyan" />}
      {notes && notes.length === 0 && <div className="text-sm text-white/50 mono">No notes yet. Be first.</div>}
      {notes && notes.length > 0 && (
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} data-testid={`note-${n.id}`} className="p-2 rounded-md border border-white/[0.06] bg-black/20">
              <div className="flex items-center gap-2 mb-1">
                <span className="mono text-[9px] px-1.5 py-0.5 rounded-full border" style={{ borderColor: `${CAT_COLOR[n.category] || "#4B9CFF"}66`, color: CAT_COLOR[n.category] || "#4B9CFF", background: `${CAT_COLOR[n.category] || "#4B9CFF"}18` }}>{n.category.toUpperCase()}</span>
                <div className="text-sm font-semibold flex-1">{n.title}</div>
              </div>
              <div className="text-xs text-white/70 whitespace-pre-wrap mb-2">{n.body}</div>
              <div className="flex items-center justify-between text-[10px] mono text-white/40">
                <span>{n.author_name || "Anon"} · {n.author_role}</span>
                {user && (
                  <button onClick={() => upvote(n)} data-testid={`upvote-${n.id}`} className="inline-flex items-center gap-1 hover:text-cyan">
                    <_ThumbsUp className="h-3 w-3" /> {n.upvotes || 0}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
